import styled, { css } from "styled-components";
import { useEffect, useState } from "react";

import Modal from "components/organisms/Modal";
import FormAddress from "components/organisms/FormAddress";
import ModalInquiryComplete from "components/layouts/Header/ModalPalette/ModalInquiryComplete";
import COLORS from "components/atoms/colors";
import { TextSm } from "components/atoms/text";
import Btn from "components/atoms/buttons";
import InputRadio from "components/atoms/input/Radio";
import InputText from "components/atoms/input/Text";

import useCreateInquiry from "apis/mutations/inquiry/useCreateInquiry";
import useCompanyMe from "apis/queries/useCompanyMe";

import Address from "types/Address";
import Pin from "types/Pin";
import PinIdInfo from "types/PinIdInfo";
import Project from "types/Project";

import getParsedJSON from "utils/getParsedJSON";
import useInput from "hooks/useInput";

import { ReactComponent as IconArrow } from "assets/images/common/arrow-down.svg";

interface Props {
  onClose: () => void;
  pinIdInfo: PinIdInfo[];
  pins: Pin[];
  project: Project;
}

type DeliverType = "defalut" | "new";

// 샘플 신청
const Inquiry = ({ onClose, pinIdInfo, pins, project }: Props) => {
  const [deliveryType, setDeliveryType] = useState<DeliverType>("defalut");
  const [sortCount, setSortCount] = useState(0);
  const [sortPins, setSortPins] = useState(pins);
  const [postCode, setPostCode] = useState<string | undefined>("");
  const [baseAddress, setBaseAddress] = useState<string | undefined>("");
  const [detailAddress, setDetailAddress] = useState<string | undefined>("");

  // 핀 목록 - 서버로 보내는 형식으로 생성
  const _pinIdInfo = pinIdInfo.map((p) => ({
    pinId: p.pinId,
    squareMeasure: +p.squareMeasure,
  }));

  // 샘플 신청 상태
  const [inquiryState, dispatch] = useInput({
    pinIdInfo: _pinIdInfo,
    receiver: "",
    address: "",
    contactNumber: "",
  });

  // 전문가 회원인 경우 내 정보 api
  const companyMe = useCompanyMe();

  // 샘플 신청 생성 api
  const createInquiry = useCreateInquiry({ projectId: project.id });

  // 배송지 타입 선택 이벤트
  const handleChangeDelivery = () => {
    if (deliveryType === "defalut") {
      if (!companyMe) return;

      dispatch({
        objectState: {
          receiver: companyMe.managerName,
          address: companyMe.address,
          contactNumber: companyMe.managerNumber,
        },
      });

      const addressObj = getParsedJSON<Address>(companyMe.address);

      if (addressObj) {
        setPostCode(addressObj?.postCode);
        setBaseAddress(addressObj?.baseAddress);
        setDetailAddress(addressObj?.detailAddress);
      }
    } else {
      setPostCode("");
      setBaseAddress("");
      setDetailAddress("");
      dispatch({ reset: true });
    }
  };

  useEffect(() => {
    handleChangeDelivery();
  }, [deliveryType, companyMe]);

  // 샘플 신청 클릭 이벤트
  const handleCreateInquiry = (onOpenModal: () => void) => {
    if (!inquiryState.receiver) return;
    if (!inquiryState.contactNumber) return;

    const newAddress = JSON.stringify({
      postCode,
      baseAddress,
      detailAddress,
    });

    const newInquiryState = { ...inquiryState, address: newAddress };

    createInquiry(newInquiryState, {
      onSuccess: () => onOpenModal(),
    });
  };

  // 목록 정렬 - 헤더 버튼 클릭 시 (0 : 오름차순, 1:내림차순, 2: 정렬기준없음)
  const handleClickSort = (sortKey: string) => {
    const keys = sortKey.split(".");

    if (sortCount === 2) {
      setSortCount(0);
      setSortPins(pins);
      return;
    }

    const _pins = [...sortPins].sort((a: any, b: any) => {
      keys.forEach((key) => {
        a = a[key];
        b = b[key];
      });

      if (sortCount === 0) return a > b ? 1 : -1;
      return a < b ? 1 : -1;
    });

    setSortPins(_pins);
    setSortCount((prev) => prev + 1);
  };

  return (
    <>
      <Head>
        <Item width="60px">이미지</Item>
        <Item width="200px">
          <button onClick={() => handleClickSort("productOption.product.name")}>
            제품명
            <IconSort preserveAspectRatio="xMidYMid meet" viewBox="0 0 18 18" />
          </button>
        </Item>
        <Item>
          <button
            onClick={() =>
              handleClickSort("productOption.product.category.name")
            }
          >
            구분
            <IconSort preserveAspectRatio="xMidYMid meet" viewBox="0 0 18 18" />
          </button>
        </Item>
        <Item>종류</Item>
        <Item>사이즈(W*L*T)</Item>
        <Item>
          <button
            onClick={() =>
              handleClickSort("productOption.product.manufacturer")
            }
          >
            제조사
            <IconSort preserveAspectRatio="xMidYMid meet" viewBox="0 0 18 18" />
          </button>
        </Item>
        <Item>원산지</Item>
        <Item>
          <button onClick={() => handleClickSort("productOption.price")}>
            단가
            <IconSort preserveAspectRatio="xMidYMid meet" viewBox="0 0 18 18" />
          </button>
        </Item>
        <Item>프로젝트 - 위치</Item>
        <Item>면적(m2)</Item>
      </Head>
      <ScrollContainer>
        <ul className="mb-10">
          {sortPins?.map((pin, i) => {
            const {
              image,
              name,
              category,
              subCategory,
              manufacturer,
              location,
              country,
              origin,
            } = pin.productOption.product;

            const { size, price } = pin.productOption;

            const scene = project.scenes.find(
              (s) => !!s.pins.find((p) => p.id === pin.id)
            );

            return (
              <li key={i} className="flex py-1">
                <Item width="60px">
                  <div className="rounded-2 overflow-hidden w-[40px] h-[40px]">
                    <img src={image} alt="" className="w-full h-full" />
                  </div>
                </Item>
                <Item width="200px">{name}</Item>
                <Item>{category?.name}</Item>
                <Item>{subCategory}</Item>
                <Item>{size}</Item>
                <Item>{manufacturer}</Item>
                <Item>{origin === "수입산" ? country : origin}</Item>
                <Item>{price?.toLocaleString()}</Item>
                <Item>
                  {project.name} - {scene?.title}
                </Item>
                <Item>{pin.squareMeasure}</Item>
              </li>
            );
          })}
        </ul>
        <FormBox>
          <div className="w-[70px]">
            <TextSm bold>배송지 선택</TextSm>
          </div>
          <div className="flex items-center gap-4">
            <InputRadio
              name="deliveryType"
              value="defalut"
              labelText="기본 배송지"
              onChange={() => setDeliveryType("defalut")}
              checked={deliveryType === "defalut"}
            />
            <InputRadio
              name="deliveryType"
              value="new"
              labelText="새로운 주소"
              onChange={() => setDeliveryType("new")}
              checked={deliveryType === "new"}
            />
          </div>
        </FormBox>
        <FormBox className="items-center">
          <div className="w-[70px]">
            <TextSm bold>받으시는 분</TextSm>
          </div>
          <div className="w-[230px]">
            <InputText
              name="receiver"
              value={inquiryState.receiver}
              onChange={(event) => dispatch({ event })}
              shape="round"
            />
          </div>
        </FormBox>
        <FormBox>
          <div className="w-[70px]">
            <TextSm bold>연락처</TextSm>
          </div>
          <div className="w-[230px]">
            <InputText
              name="contactNumber"
              value={inquiryState.contactNumber}
              onChange={(event) => dispatch({ event })}
              shape="round"
            />
          </div>
        </FormBox>
        <FormBox>
          <div className="w-[70px]">
            <TextSm bold>배송지 주소</TextSm>
          </div>
          <FormAddress
            addressState={{ postCode, baseAddress, detailAddress }}
            chageEvent={{
              onChageDetail: (v) => setDetailAddress(v),
              onChageBase: (v) => setBaseAddress(v),
              onChagePostCode: (v) => setPostCode(v),
            }}
            shape="round"
            className="gap-2"
          />
        </FormBox>
      </ScrollContainer>
      <div className="flex justify-between items-center mt-10">
        <div>
          <TextSm className="text-black-80%" bold>
            총 {pinIdInfo.length}개의 샘플을 문의 하시겠습니까? <br />
          </TextSm>
          <p className="text-black-60%">
            신청자 본인의 정보를 해당 제품 판매사에게 공유하는 것에 동의합니다.
          </p>
        </div>
        <div className="flex gap-5">
          <Btn.Red
            onClick={onClose}
            size="medium"
            className="w-[100px]"
            radiusFull
          >
            취소
          </Btn.Red>
          <Modal
            activator={(onOpen) => (
              <Btn.Blue
                onClick={() => handleCreateInquiry(onOpen)}
                size="medium"
                className="w-[100px]"
                radiusFull
              >
                확인
              </Btn.Blue>
            )}
            modal={(onCloseModal) => (
              <ModalInquiryComplete
                onCloseModal={() => {
                  onClose();
                  onCloseModal();
                }}
              />
            )}
            backdropHidden
          />
        </div>
      </div>
    </>
  );
};

const Head = styled.div`
  flex: none;
  display: flex;
  margin-bottom: 4px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${COLORS.black["40%"]};
  background-color: #fff;
  padding-top: 40px;

  & > div {
    color: ${COLORS.black["60%"]};
  }
`;

const ScrollContainer = styled.div`
  position: relative;
  flex: 1;
  overflow-y: scroll;
  margin-right: -8px;
`;

const Item = styled.div<{ width?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => width || "auto"};
  text-align: center;

  & > button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ width }) =>
    !width &&
    css`
      flex: 1;
    `};
`;

const IconSort = styled(IconArrow)`
  width: 12px;
  height: 12px;
  path {
    fill: ${COLORS.black["60%"]};
  }
`;

const FormBox = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

export default Inquiry;
